/* ==========================================================================
   GYMJET — collection.js (collection + search pages)
   AJAX filtering/sorting with Shopify's Section Rendering API,
   "load more" pagination and grid density.
   ========================================================================== */
(() => {
  'use strict';

  const GJ = (window.GJ = window.GJ || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const DENSITY_KEY = 'gj_grid_density';

  function init(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = '1';
    const sectionId = root.dataset.sectionId;
    let controller;

    const regions = ['[data-results]', '[data-active-facets]', '[data-results-count]'];

    const paramsFromForm = () => {
      const form = $('[data-facets-form]', root);
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
      }
      return params;
    };

    const render = async (url, { push = true } = {}) => {
      const results = $('[data-results]', root);
      if (results) results.classList.add('is-loading');
      if (controller) controller.abort();
      controller = new AbortController();
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(fetchUrl.toString(), { signal: controller.signal });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        regions.forEach((sel) => {
          const fresh = $(sel, doc);
          const current = $(sel, root);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });

        const freshResults = $('[data-results]', doc);
        if (freshResults && results) results.dataset.resultsTotal = freshResults.dataset.resultsTotal;

        /* Keep the user's open/closed facet groups */
        const wrap = $('[data-facets-wrap]', root);
        const freshForm = $('[data-facets-form]', doc);
        if (wrap && freshForm) {
          const open = $$('details[data-facet]', wrap)
            .filter((d) => d.open)
            .map((d) => d.dataset.facet);
          const focusedName = document.activeElement && wrap.contains(document.activeElement) ? document.activeElement.name + '|' + document.activeElement.value : null;
          wrap.innerHTML = freshForm.outerHTML;
          $$('details[data-facet]', wrap).forEach((d) => {
            if (open.includes(d.dataset.facet)) d.open = true;
          });
          if (focusedName) {
            const [name, value] = focusedName.split('|');
            const again = $$('input', wrap).find((i) => i.name === name && (i.type === 'number' || i.value === value));
            if (again) again.focus({ preventScroll: true });
          }
        }

        const freshCount = $('[data-filter-count]', doc);
        const countEl = $('[data-filter-count]', root);
        if (freshCount && countEl) {
          countEl.textContent = freshCount.textContent;
          countEl.hidden = freshCount.hasAttribute('hidden');
        }
        const freshShow = $('[data-show-results]', doc);
        const showBtn = $('[data-show-results]', root);
        if (freshShow && showBtn) showBtn.textContent = freshShow.textContent;

        const freshSort = $('[data-sort-select]', doc);
        const sortSelect = $('[data-sort-select]', root);
        if (freshSort && sortSelect) sortSelect.value = freshSort.value;

        applyDensity();
        if (GJ.reveal) GJ.reveal(root);
        if (GJ.wishlist) GJ.wishlist.sync();

        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({ gjFacets: true }, '', clean.toString());
        }
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url;
      } finally {
        if (results) results.classList.remove('is-loading');
      }
    };

    const submit = () => {
      const params = paramsFromForm();
      render(`${window.location.pathname}?${params.toString()}`);
    };

    let timer;
    root.addEventListener('change', (e) => {
      if (e.target.closest('[data-facets-form]')) {
        clearTimeout(timer);
        timer = setTimeout(submit, e.target.type === 'number' ? 600 : 150);
      }
      if (e.target.matches('[data-sort-select]')) {
        const radio = $$('[data-facets-form] input[name="sort_by"]', root).find((r) => r.value === e.target.value);
        if (radio) radio.checked = true;
        submit();
      }
    });

    root.addEventListener('submit', (e) => {
      if (e.target.matches('[data-facets-form]')) {
        e.preventDefault();
        submit();
      }
    });

    root.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-url]');
      if (link) {
        e.preventDefault();
        render(link.href);
        return;
      }
      const more = e.target.closest('[data-load-more]');
      if (more) {
        e.preventDefault();
        loadMore(more);
      }
    });

    window.addEventListener('popstate', () => render(window.location.href, { push: false }));

    async function loadMore(btn) {
      if (btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      const url = new URL(btn.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(url.toString());
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = $('[data-grid]', root);
        const freshGrid = $('[data-grid]', doc);
        if (grid && freshGrid) {
          const start = grid.children.length;
          Array.from(freshGrid.children).forEach((card, i) => {
            card.style.setProperty('--i', i % 8);
            grid.appendChild(card);
          });
          const firstNew = grid.children[start];
          const link = firstNew && firstNew.querySelector('a[href]:not([tabindex="-1"])');
          if (link) link.focus({ preventScroll: true });
        }
        const pager = $('[data-pager]', root);
        const freshPager = $('[data-pager]', doc);
        if (pager && freshPager) pager.innerHTML = freshPager.innerHTML;
        applyDensity();
        if (GJ.reveal) GJ.reveal(root);
        if (GJ.wishlist) GJ.wishlist.sync();
      } catch (err) {
        window.location.href = btn.href;
      }
    }

    /* Grid density */
    const applyDensity = () => {
      const grid = $('[data-grid]', root);
      if (!grid) return;
      let saved = {};
      try {
        saved = JSON.parse(localStorage.getItem(DENSITY_KEY)) || {};
      } catch (e) {
        saved = {};
      }
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      const value = desktop ? saved.d || '4' : saved.m || '2';
      grid.dataset.density = value === '4' || value === '2' ? '' : value;
      $$('[data-density-set]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.densitySet === value)));
    };

    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-density-set]');
      if (!b) return;
      let saved = {};
      try {
        saved = JSON.parse(localStorage.getItem(DENSITY_KEY)) || {};
      } catch (err) {
        saved = {};
      }
      if (b.hasAttribute('data-grid-desktop')) saved.d = b.dataset.densitySet;
      else saved.m = b.dataset.densitySet;
      try {
        localStorage.setItem(DENSITY_KEY, JSON.stringify(saved));
      } catch (err) {
        /* ignore */
      }
      applyDensity();
    });

    window.matchMedia('(min-width: 990px)').addEventListener('change', applyDensity);
    applyDensity();
  }

  const boot = (scope = document) => $$('[data-collection]', scope).forEach(init);
  boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
